import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Plus,
  Webhook,
  Pencil,
  Trash2,
  RefreshCw,
  Copy,
  Check,
  Zap,
  AlertCircle,
  ChevronRight,
  Shield,
  Server,
  Video as VideoIcon,
} from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { webhooksApi, systemWebhooksApi } from "@/lib/api";
import { useAuth } from "@/lib/auth";

const ALL_EVENTS = [
  { id: "project.created", label: "Project created" },
  { id: "project.updated", label: "Project updated" },
  { id: "project.deleted", label: "Project deleted" },
  { id: "day.created", label: "Day added to call sheet" },
  { id: "day.updated", label: "Day edited (call time, weather, crew, etc.)" },
  { id: "day.deleted", label: "Day removed" },
];

export default function AdminWebhooksPage() {
  const { user: me } = useAuth();
  const isAdmin = me?.role === "admin";
  const [endpoints, setEndpoints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [deliveriesFor, setDeliveriesFor] = useState(null);

  const reload = async () => {
    setLoading(true);
    try {
      setEndpoints(await webhooksApi.list());
    } catch {
      toast.error("Failed to load webhook endpoints");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!me) return;
    if (!isAdmin) {
      setLoading(false);
      return;
    }
    reload();
  }, [isAdmin, me]);

  if (me && !isAdmin) {
    return (
      <div className="max-w-md mx-auto text-center py-24 px-6">
        <Shield className="w-12 h-12 mx-auto mb-4 text-zinc-700" />
        <h2 className="font-heading text-2xl font-light text-zinc-100 mb-2">
          Admin access required
        </h2>
        <p className="text-sm text-zinc-500 mb-6">
          Only admins can manage outbound webhooks.
        </p>
        <Link
          to="/launchpad"
          data-testid="back-to-launchpad-link"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-sm border border-zinc-800 text-sm text-zinc-300 hover:text-amber-400 hover:border-amber-500/40 transition-colors"
        >
          Back to Launchpad
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-6 lg:px-10 py-8">
      <div className="flex items-end justify-between gap-4 flex-wrap mb-2">
        <div>
          <div className="text-overline text-zinc-500">Admin</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-light text-zinc-100 mt-1 flex items-center gap-3">
            <Webhook className="w-7 h-7 text-amber-500" />
            Webhooks
          </h1>
        </div>
        <Button
          data-testid="new-webhook-btn"
          onClick={() => {
            setEditingId(null);
            setEditorOpen(true);
          }}
          className="bg-amber-500 hover:bg-amber-400 text-black"
        >
          <Plus className="w-4 h-4 mr-1.5" /> New Endpoint
        </Button>
      </div>

      <p className="text-zinc-500 text-sm max-w-2xl leading-relaxed mt-2 mb-8">
        Outbound webhooks notify external apps (like the Gear Inventory app)
        when projects or call sheet days are created, updated, or deleted. Each
        endpoint receives signed POST requests so the receiver can verify
        authenticity. Payloads match the{" "}
        <code className="text-amber-500/90 font-mono text-xs">
          /api/sync/projects
        </code>{" "}
        shape.
      </p>

      <SystemIntegrations />

      <div className="text-overline text-zinc-500 mt-10 mb-3 flex items-center gap-2">
        <Webhook className="w-3.5 h-3.5" /> User endpoints
      </div>

      {loading ? (
        <div className="text-zinc-500 text-sm">Loading…</div>
      ) : endpoints.length === 0 ? (
        <div className="rounded-md border border-dashed border-zinc-800 p-10 text-center bg-zinc-900/40">
          <Webhook className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
          <div className="text-zinc-300 font-medium">No endpoints yet</div>
          <div className="text-zinc-500 text-sm mt-1.5">
            Add your first endpoint to start broadcasting events.
          </div>
        </div>
      ) : (
        <div className="border border-zinc-800 rounded-md overflow-hidden bg-zinc-900">
          {endpoints.map((ep, i) => (
            <div
              key={ep.id}
              data-testid={`webhook-row-${ep.id}`}
              className={`grid grid-cols-12 gap-4 items-center px-5 py-4 ${
                i < endpoints.length - 1 ? "border-b border-zinc-800/60" : ""
              }`}
            >
              <div className="col-span-12 md:col-span-5 min-w-0">
                <div className="flex items-center gap-2">
                  <div
                    className={`w-2 h-2 rounded-full shrink-0 ${
                      !ep.active
                        ? "bg-zinc-600"
                        : ep.last_status === "failed"
                          ? "bg-rose-500"
                          : ep.last_status === "success"
                            ? "bg-emerald-500"
                            : "bg-amber-500/50"
                    }`}
                    title={
                      !ep.active
                        ? "Disabled"
                        : ep.last_status === "failed"
                          ? `Last delivery failed: ${ep.last_error || "unknown error"}`
                          : ep.last_status === "success"
                            ? "Last delivery succeeded"
                            : "No deliveries yet"
                    }
                  />
                  <div className="font-mono text-sm text-zinc-100 truncate" title={ep.url}>
                    {ep.url}
                  </div>
                </div>
                {ep.description && (
                  <div className="text-overline text-zinc-500 mt-1 truncate">
                    {ep.description}
                  </div>
                )}
              </div>

              <div className="col-span-6 md:col-span-3 text-xs">
                <div className="text-zinc-500 uppercase tracking-wider text-[10px] mb-0.5">
                  Events
                </div>
                <div className="text-zinc-300 truncate">
                  {ep.events?.includes("*")
                    ? "All events"
                    : (ep.events || []).join(", ")}
                </div>
              </div>

              <div className="col-span-6 md:col-span-2 text-xs">
                <div className="text-zinc-500 uppercase tracking-wider text-[10px] mb-0.5">
                  Last delivery
                </div>
                <div className="text-zinc-300">
                  {ep.last_delivery_at ? fmtAgo(ep.last_delivery_at) : "—"}
                </div>
              </div>

              <div className="col-span-12 md:col-span-2 flex justify-end gap-1">
                <button
                  data-testid={`webhook-test-${ep.id}`}
                  onClick={async () => {
                    const tid = toast.loading("Sending test event…");
                    try {
                      const r = await webhooksApi.test(ep.id);
                      toast.dismiss(tid);
                      if (r.ok)
                        toast.success("Test event delivered successfully", {
                          description: `Delivered at ${fmtTime(r.delivered_at)}`,
                        });
                      else
                        toast.error("Test event failed", {
                          description: r.error || "Receiver did not return 2xx",
                        });
                      reload();
                    } catch {
                      toast.dismiss(tid);
                      toast.error("Failed to send test");
                    }
                  }}
                  title="Send test event"
                  className="p-2 text-zinc-400 hover:text-amber-500 hover:bg-zinc-800 rounded-sm transition-colors"
                >
                  <Zap className="w-4 h-4" />
                </button>
                <button
                  data-testid={`webhook-deliveries-${ep.id}`}
                  onClick={() => setDeliveriesFor(ep)}
                  title="View delivery log"
                  className="p-2 text-zinc-400 hover:text-amber-500 hover:bg-zinc-800 rounded-sm transition-colors"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
                <button
                  data-testid={`webhook-edit-${ep.id}`}
                  onClick={() => {
                    setEditingId(ep.id);
                    setEditorOpen(true);
                  }}
                  title="Edit endpoint"
                  className="p-2 text-zinc-400 hover:text-amber-500 hover:bg-zinc-800 rounded-sm transition-colors"
                >
                  <Pencil className="w-4 h-4" />
                </button>
                <button
                  data-testid={`webhook-delete-${ep.id}`}
                  onClick={async () => {
                    if (!confirm(`Delete this endpoint? ${ep.url}`)) return;
                    try {
                      await webhooksApi.remove(ep.id);
                      toast.success("Endpoint deleted");
                      reload();
                    } catch {
                      toast.error("Failed to delete");
                    }
                  }}
                  title="Delete endpoint"
                  className="p-2 text-zinc-400 hover:text-rose-400 hover:bg-rose-500/10 rounded-sm transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <EndpointEditor
        open={editorOpen}
        onOpenChange={(o) => {
          setEditorOpen(o);
          if (!o) setEditingId(null);
        }}
        endpointId={editingId}
        onSaved={reload}
      />

      <DeliveriesDrawer
        endpoint={deliveriesFor}
        onClose={() => setDeliveriesFor(null)}
      />
    </div>
  );
}

function EndpointEditor({ open, onOpenChange, endpointId, onSaved }) {
  const isEdit = !!endpointId;
  const [url, setUrl] = useState("");
  const [description, setDescription] = useState("");
  const [active, setActive] = useState(true);
  const [onlyWhenShoot, setOnlyWhenShoot] = useState(true);
  const [allEvents, setAllEvents] = useState(true);
  const [selectedEvents, setSelectedEvents] = useState(
    ALL_EVENTS.map((e) => e.id)
  );
  const [saving, setSaving] = useState(false);
  const [revealedSecret, setRevealedSecret] = useState("");
  const [secretCopied, setSecretCopied] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (isEdit) {
      (async () => {
        try {
          const ep = await webhooksApi.get(endpointId);
          setUrl(ep.url || "");
          setDescription(ep.description || "");
          setActive(!!ep.active);
          setOnlyWhenShoot(!!ep.only_when_shoot_scheduled);
          if (ep.events?.includes("*")) {
            setAllEvents(true);
            setSelectedEvents(ALL_EVENTS.map((e) => e.id));
          } else {
            setAllEvents(false);
            setSelectedEvents(ep.events || []);
          }
          setRevealedSecret(ep.secret || "");
        } catch {
          toast.error("Failed to load endpoint");
        }
      })();
    } else {
      setUrl("");
      setDescription("");
      setActive(true);
      setOnlyWhenShoot(true);
      setAllEvents(true);
      setSelectedEvents(ALL_EVENTS.map((e) => e.id));
      setRevealedSecret("");
    }
    setSecretCopied(false);
  }, [open, endpointId, isEdit]);

  const handleSave = async () => {
    if (!url || !/^https?:\/\//.test(url)) {
      toast.error("URL must start with http:// or https://");
      return;
    }
    setSaving(true);
    try {
      const events = allEvents ? ["*"] : selectedEvents;
      if (events.length === 0) {
        toast.error("Pick at least one event or enable All events");
        setSaving(false);
        return;
      }
      if (isEdit) {
        await webhooksApi.update(endpointId, {
          url,
          description,
          events,
          active,
          only_when_shoot_scheduled: onlyWhenShoot,
        });
        toast.success("Endpoint updated");
      } else {
        const created = await webhooksApi.create({
          url,
          description,
          events,
          active,
          only_when_shoot_scheduled: onlyWhenShoot,
        });
        setRevealedSecret(created.secret);
        toast.success("Endpoint created — copy the secret below");
      }
      onSaved();
      // For create, keep dialog open so the user can copy the secret
      if (isEdit) onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleRotateSecret = async () => {
    if (
      !confirm(
        "Rotate the signing secret? The Gear app will need to be updated with the new value before its next verification check passes."
      )
    )
      return;
    try {
      const r = await webhooksApi.rotateSecret(endpointId);
      setRevealedSecret(r.secret);
      setSecretCopied(false);
      toast.success("Secret rotated — copy the new value");
    } catch {
      toast.error("Rotate failed");
    }
  };

  const copySecret = async () => {
    try {
      await navigator.clipboard.writeText(revealedSecret);
      setSecretCopied(true);
      setTimeout(() => setSecretCopied(false), 2500);
      toast.success("Secret copied");
    } catch {
      window.prompt("Copy secret:", revealedSecret);
    }
  };

  const toggleEvent = (id) => {
    setSelectedEvents((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl font-light flex items-center gap-2">
            <Webhook className="w-5 h-5 text-amber-500" />
            {isEdit ? "Edit endpoint" : "New endpoint"}
          </DialogTitle>
          <DialogDescription className="text-zinc-500">
            {isEdit
              ? "Update where events get sent. Secret can be rotated below if compromised."
              : "Receivers should respond with a 2xx status code within 10 seconds. Three delivery attempts will be made on failure."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 pt-2">
          <div>
            <Label htmlFor="wh-url" className="text-overline text-zinc-400 mb-1.5 block">
              Endpoint URL
            </Label>
            <Input
              id="wh-url"
              data-testid="webhook-url-input"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://gear.example.com/webhooks/whiplash-pm"
              className="bg-zinc-950 border-zinc-800 focus-visible:ring-amber-500 font-mono text-sm"
            />
          </div>

          <div>
            <Label htmlFor="wh-desc" className="text-overline text-zinc-400 mb-1.5 block">
              Description (optional)
            </Label>
            <Input
              id="wh-desc"
              data-testid="webhook-desc-input"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="e.g. Gear Inventory app — production receiver"
              className="bg-zinc-950 border-zinc-800 focus-visible:ring-amber-500"
            />
          </div>

          <div className="flex items-center justify-between">
            <div>
              <Label className="text-overline text-zinc-400">Active</Label>
              <div className="text-xs text-zinc-500 mt-0.5">
                Inactive endpoints receive no deliveries
              </div>
            </div>
            <Switch
              data-testid="webhook-active-switch"
              checked={active}
              onCheckedChange={setActive}
            />
          </div>

          <div className="flex items-center justify-between">
            <div className="pr-4">
              <Label className="text-overline text-zinc-400">
                Only when shoot scheduled
              </Label>
              <div className="text-xs text-zinc-500 mt-0.5 leading-snug">
                Skip <code className="font-mono text-amber-500/80">project.created</code> /{" "}
                <code className="font-mono text-amber-500/80">project.updated</code>{" "}
                deliveries for projects with no shoot dates. Prevents Gear
                from auto-creating records for projects still in planning.
              </div>
            </div>
            <Switch
              data-testid="webhook-shoot-gate-switch"
              checked={onlyWhenShoot}
              onCheckedChange={setOnlyWhenShoot}
            />
          </div>

          <div className="border-t border-zinc-800 pt-4">
            <div className="flex items-center justify-between mb-3">
              <div>
                <Label className="text-overline text-zinc-400">Events to subscribe</Label>
                <div className="text-xs text-zinc-500 mt-0.5">
                  All events recommended for Gear sync
                </div>
              </div>
              <Switch
                data-testid="webhook-all-events-switch"
                checked={allEvents}
                onCheckedChange={setAllEvents}
              />
            </div>
            {!allEvents && (
              <div className="space-y-2 pl-1">
                {ALL_EVENTS.map((ev) => (
                  <label
                    key={ev.id}
                    className="flex items-start gap-3 py-1 cursor-pointer hover:bg-zinc-900/50 -mx-2 px-2 rounded-sm"
                  >
                    <input
                      type="checkbox"
                      checked={selectedEvents.includes(ev.id)}
                      onChange={() => toggleEvent(ev.id)}
                      className="mt-1 accent-amber-500"
                      data-testid={`webhook-event-${ev.id}`}
                    />
                    <div>
                      <div className="font-mono text-xs text-amber-400">{ev.id}</div>
                      <div className="text-xs text-zinc-500">{ev.label}</div>
                    </div>
                  </label>
                ))}
              </div>
            )}
          </div>

          {revealedSecret && (
            <div className="border-t border-zinc-800 pt-4">
              <Label className="text-overline text-zinc-400 mb-1.5 block">
                Signing secret
              </Label>
              <div className="text-xs text-amber-500/90 mb-2 flex items-start gap-1.5 leading-relaxed">
                <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                <span>
                  Store this in the Gear app&apos;s environment as{" "}
                  <code className="font-mono">WHIPLASH_PM_WEBHOOK_SECRET</code>. It
                  is used to verify the <code className="font-mono">X-Whiplash-Signature</code>{" "}
                  header on every request.
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Textarea
                  readOnly
                  value={revealedSecret}
                  data-testid="webhook-secret-display"
                  className="bg-zinc-950 border-zinc-800 font-mono text-xs min-h-[60px] resize-none"
                />
                <button
                  onClick={copySecret}
                  data-testid="webhook-copy-secret-btn"
                  className="shrink-0 p-2.5 bg-zinc-900 border border-zinc-800 hover:bg-zinc-800 rounded-sm text-zinc-300"
                  title="Copy secret"
                >
                  {secretCopied ? (
                    <Check className="w-4 h-4 text-emerald-500" />
                  ) : (
                    <Copy className="w-4 h-4" />
                  )}
                </button>
              </div>
              {isEdit && (
                <button
                  onClick={handleRotateSecret}
                  className="mt-2 inline-flex items-center gap-1.5 text-xs text-zinc-500 hover:text-amber-500"
                >
                  <RefreshCw className="w-3 h-3" /> Rotate secret
                </button>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="pt-4">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="bg-transparent border-zinc-800 text-zinc-300 hover:bg-zinc-900"
          >
            Close
          </Button>
          <Button
            data-testid="webhook-save-btn"
            onClick={handleSave}
            disabled={saving}
            className="bg-amber-500 hover:bg-amber-400 text-black"
          >
            {saving ? "Saving…" : isEdit ? "Save changes" : "Create endpoint"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DeliveriesDrawer({ endpoint, onClose }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!endpoint) return;
    setLoading(true);
    webhooksApi
      .deliveries(endpoint.id)
      .then(setItems)
      .catch(() => toast.error("Failed to load deliveries"))
      .finally(() => setLoading(false));
  }, [endpoint]);

  if (!endpoint) return null;

  return (
    <Dialog open={!!endpoint} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 sm:max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl font-light">
            Delivery log
          </DialogTitle>
          <DialogDescription className="text-zinc-500 font-mono text-xs truncate">
            {endpoint.url}
          </DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="text-zinc-500 text-sm">Loading…</div>
        ) : items.length === 0 ? (
          <div className="text-zinc-500 text-sm italic">No deliveries yet</div>
        ) : (
          <div className="space-y-1.5">
            {items.map((d) => (
              <div
                key={d.id}
                className="grid grid-cols-[auto,1fr,auto] items-center gap-3 p-3 rounded-sm border border-zinc-800/60 bg-zinc-900/50 text-xs"
              >
                <div
                  className={`w-1.5 h-8 rounded-sm ${
                    d.status === "success" ? "bg-emerald-500" : "bg-rose-500"
                  }`}
                />
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-amber-400">{d.event_type}</span>
                    <span className="text-zinc-600">·</span>
                    <span className="text-zinc-500">{fmtAgo(d.created_at)}</span>
                    {d.attempts > 1 && (
                      <span className="text-zinc-600">· {d.attempts} attempts</span>
                    )}
                  </div>
                  {d.error && (
                    <div className="text-rose-400/90 truncate mt-0.5">{d.error}</div>
                  )}
                </div>
                <div className="text-zinc-500 font-mono">
                  {d.response_code ? `HTTP ${d.response_code}` : "—"}
                </div>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function SystemIntegrations() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deliveriesFor, setDeliveriesFor] = useState(null);

  const load = async () => {
    try {
      setItems(await systemWebhooksApi.list());
    } catch {
      toast.error("Failed to load system integrations");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  if (loading) {
    return (
      <div className="text-zinc-500 text-sm mb-6">Loading system integrations…</div>
    );
  }
  if (!items.length) return null;

  return (
    <>
      <div className="text-overline text-zinc-500 mb-3 flex items-center gap-2">
        <Server className="w-3.5 h-3.5" /> System integrations
      </div>
      <div className="border border-zinc-800 rounded-md bg-zinc-900 divide-y divide-zinc-800/60 mb-2">
        {items.map((it) => (
          <SystemIntegrationRow
            key={it.key}
            item={it}
            onRefresh={load}
            onShowDeliveries={() => setDeliveriesFor(it)}
          />
        ))}
      </div>
      <p className="text-[11px] text-zinc-600 leading-relaxed max-w-2xl mb-2">
        System integrations are configured server-side via environment
        variables. They can&apos;t be edited from the UI, but you can send test
        events and inspect delivery history here.
      </p>

      <SystemDeliveriesDrawer
        item={deliveriesFor}
        onClose={() => setDeliveriesFor(null)}
      />
    </>
  );
}

function SystemIntegrationRow({ item, onRefresh, onShowDeliveries }) {
  const [testing, setTesting] = useState(false);
  const isConfigured = item.configured;
  const status = item.last_status;
  const dot = !isConfigured
    ? "bg-zinc-700"
    : status === "success"
      ? "bg-emerald-500"
      : status === "failed"
        ? "bg-rose-500"
        : "bg-amber-500/60";
  const dotTitle = !isConfigured
    ? "Not configured (env var missing)"
    : status === "success"
      ? "Last delivery succeeded"
      : status === "failed"
        ? `Last delivery failed: ${item.last_error || "unknown"}`
        : "No deliveries yet";

  const sendTest = async () => {
    setTesting(true);
    const tid = toast.loading("Sending test event…");
    try {
      const r = await systemWebhooksApi.test(item.key);
      toast.dismiss(tid);
      if (r.ok) {
        toast.success("Test delivered", {
          description: `Response ${r.response_code} at ${new Date(r.delivered_at).toLocaleTimeString()}`,
        });
      } else {
        toast.error("Test failed", {
          description: r.error || `HTTP ${r.response_code}`,
        });
      }
      onRefresh();
    } catch (e) {
      toast.dismiss(tid);
      toast.error(e?.response?.data?.detail || "Test failed");
    } finally {
      setTesting(false);
    }
  };

  return (
    <div
      data-testid={`system-webhook-row-${item.key}`}
      className="grid grid-cols-12 gap-4 items-center px-5 py-4"
    >
      <div className="col-span-12 md:col-span-5 min-w-0">
        <div className="flex items-center gap-2">
          <div className={`w-2 h-2 rounded-full shrink-0 ${dot}`} title={dotTitle} />
          <VideoIcon className="w-3.5 h-3.5 text-zinc-500 shrink-0" />
          <div className="text-sm text-zinc-100 truncate font-medium">
            {item.label}
          </div>
        </div>
        <div className="font-mono text-[11px] text-zinc-500 truncate mt-1 ml-4" title={item.url}>
          {item.url || <span className="italic text-zinc-600">Not configured</span>}
        </div>
      </div>

      <div className="col-span-6 md:col-span-3 text-xs">
        <div className="text-zinc-500 uppercase tracking-wider text-[10px] mb-0.5">
          Signature
        </div>
        <div className="text-zinc-300">
          {item.signing_enabled ? "HMAC-SHA256 signed" : "Unsigned"}
        </div>
      </div>

      <div className="col-span-6 md:col-span-2 text-xs">
        <div className="text-zinc-500 uppercase tracking-wider text-[10px] mb-0.5">
          Last delivery
        </div>
        <div className="text-zinc-300">
          {item.last_delivery_at ? fmtAgo(item.last_delivery_at) : "—"}
        </div>
        {item.last_response_code && (
          <div className="text-zinc-500 font-mono text-[10px]">
            HTTP {item.last_response_code}
          </div>
        )}
      </div>

      <div className="col-span-12 md:col-span-2 flex justify-end gap-1">
        <button
          data-testid={`system-webhook-test-${item.key}`}
          onClick={sendTest}
          disabled={testing || !isConfigured}
          title="Send test event"
          className="p-2 text-zinc-400 hover:text-amber-500 hover:bg-zinc-800 rounded-sm transition-colors disabled:opacity-40 disabled:hover:text-zinc-400 disabled:hover:bg-transparent"
        >
          <Zap className="w-4 h-4" />
        </button>
        <button
          data-testid={`system-webhook-deliveries-${item.key}`}
          onClick={onShowDeliveries}
          title="View delivery log"
          className="p-2 text-zinc-400 hover:text-amber-500 hover:bg-zinc-800 rounded-sm transition-colors"
        >
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

function SystemDeliveriesDrawer({ item, onClose }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!item) return;
    setLoading(true);
    systemWebhooksApi
      .deliveries(item.key)
      .then(setItems)
      .catch(() => toast.error("Failed to load deliveries"))
      .finally(() => setLoading(false));
  }, [item]);

  if (!item) return null;

  return (
    <Dialog open={!!item} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 sm:max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl font-light">
            {item.label} — delivery log
          </DialogTitle>
          <DialogDescription className="text-zinc-500 font-mono text-xs truncate">
            {item.url}
          </DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="text-zinc-500 text-sm">Loading…</div>
        ) : items.length === 0 ? (
          <div className="text-zinc-500 text-sm italic">No deliveries yet</div>
        ) : (
          <div className="space-y-1.5">
            {items.map((d) => (
              <div
                key={d.id}
                className="grid grid-cols-[auto,1fr,auto] items-center gap-3 p-3 rounded-sm border border-zinc-800/60 bg-zinc-900/50 text-xs"
              >
                <div
                  className={`w-1.5 h-8 rounded-sm ${
                    d.status === "success" ? "bg-emerald-500" : "bg-rose-500"
                  }`}
                />
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-amber-400">{d.event_type}</span>
                    <span className="text-zinc-600">·</span>
                    <span className="text-zinc-500">{fmtAgo(d.created_at)}</span>
                    {d.payload_summary?.title && (
                      <>
                        <span className="text-zinc-600">·</span>
                        <span className="text-zinc-400 truncate">
                          {d.payload_summary.title}
                        </span>
                      </>
                    )}
                  </div>
                  {d.error && (
                    <div className="text-rose-400/90 truncate mt-0.5">{d.error}</div>
                  )}
                  {d.payload_summary?.source_project_name && (
                    <div className="text-zinc-600 truncate mt-0.5">
                      Project: {d.payload_summary.source_project_name}
                    </div>
                  )}
                </div>
                <div className="text-zinc-500 font-mono">
                  {d.response_code ? `HTTP ${d.response_code}` : "—"}
                </div>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function fmtAgo(iso) {
  if (!iso) return "—";
  try {
    const then = new Date(iso).getTime();
    const diff = Math.max(0, Date.now() - then) / 1000;
    if (diff < 60) return `${Math.floor(diff)}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return new Date(iso).toLocaleDateString();
  } catch {
    return iso;
  }
}

function fmtTime(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleTimeString();
  } catch {
    return iso;
  }
}
